import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Firebase Realtime Database
import { ref, get, onValue, query, orderByChild, equalTo } from "firebase/database"; 
import { db } from "../../assets/firebase/firebaseconfig";

// Components & Utils
import RatingSection from "./Components/RatingSection";
import Loading from "../Loading";
import Info from "../../assets/js/info"; // Component hiển thị mô tả (dangerouslySetInnerHTML)
import { createSlug } from "../../assets/js/slug";
import "./styles.css"
const ProductDetailPage = ({ headerHeight }) => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // States
  const [product, setProduct] = useState(null);
  const [productId, setProductId] = useState(null); // ID thực tế từ Firebase
  const [allProducts, setAllProducts] = useState([]);
  const [comments, setComments] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  // 1. Lấy toàn bộ danh sách để làm "Sản phẩm liên quan"
  useEffect(() => {
    const productsRef = ref(db, "products");
    get(productsRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const productList = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setAllProducts(productList);
      }
    });
  }, []);

  // 2. Lấy chi tiết sản phẩm và bình luận dựa trên SLUG từ URL
  useEffect(() => {
    const getProductAndComments = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        const productsRef = ref(db, "products");
        
        // Tạo truy vấn tìm sản phẩm có field 'slug' khớp với URL
        const productQuery = query(productsRef, orderByChild("slug"), equalTo(slug));
        const snapshot = await get(productQuery);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const actualId = Object.keys(data)[0]; // Lấy Key (ID) thực tế
          const productData = data[actualId];
          
          setProductId(actualId); // Để truyền vào RatingSection
          setProduct(productData);

          // Lấy bình luận dựa trên ID thực tế (Comments thường lưu theo ID cố định)
          const commentsRef = ref(db, `comments/${actualId}`);
          onValue(commentsRef, (commentSnapshot) => {
            const commentData = commentSnapshot.val();
            if (commentData) {
              const loadedComments = Object.keys(commentData).map((key) => ({
                id: key,
                ...commentData[key],
              }));
              setComments(loadedComments);
            } else {
              setComments([]);
            }
          });
        } else {
          setProduct(null);
          console.error("Sản phẩm không tồn tại với slug:", slug);
        }
      } catch (error) {
        console.error("Lỗi lấy dữ liệu:", error);
      } finally {
        setLoading(false);
      }
    };

    getProductAndComments();
    window.scrollTo(0, 0); // Luôn cuộn lên đầu khi vào trang mới
  }, [slug]);
  
  // Logic xử lý sản phẩm liên quan (Lọc theo danh mục và bỏ sản phẩm hiện tại)
  const relatedProducts = allProducts
    .filter((p) => product && p.category === product.category && p.slug !== slug)
    .sort(() => Math.random() - 0.5)
    .slice(0, 6);

  // Hiệu ứng Reveal on Scroll cho sản phẩm liên quan
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("show");
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".reveal-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [relatedProducts]);

  const handleQuantityChange = (type) => {
    if (type === "plus") setQuantity(q => q + 1);
    else if (type === "minus" && quantity > 1) setQuantity(q => q - 1);
  };

  if (loading) return <Loading />;
  
  if (!product) {
    return (
      <div className="habako-skeleton" style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Đang tải sản phẩm hoặc không tìm thấy...
      </div>
    );
  }

  return (
    <div className="page-product-detail" style={{ backgroundColor: "#f5f5f5" }}>
      <Helmet>
        <title>{product.name} | Habako M&E</title>
        <meta name="description" content={product.name?.replace(/<[^>]*>?/gm, '').substring(0, 150)} />
      <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "product", // Hoặc "Product" nếu là trang sản phẩm
      "headline": product.title,
      "description": product.description,
      "image": product.image,
      "author": {
        "@type": "Organization",
        "name": "Habako M&E"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Habako M&E",
        "logo": {
          "@type": "ImageObject",
          "url": "https://habakome-6d2ee.web.app/static/media/logo1.bded67026a0b99b52999.png"
        }
      },
      "datePublished": new Date(product.createdAt).toISOString()
    })}
  </script>
      </Helmet>

      <div className="section-padding">
        {/* Breadcrumb chuẩn Shopee */}
        <div className="breadcrumb-shopee">
          <span onClick={() => navigate("/")}>HABAKO</span> &gt;
          <span onClick={() => navigate("/san-pham")}> Sản phẩm</span> &gt;
          <span className="current"> {product.name}</span>
        </div>

        <div className="product-detail-wrapper">
          {/* Bên trái: Hình ảnh */}
          <div className="product-detail-left">
            <div className="main-image">
              <img src={product.mediaUrl} alt={product.name} />
            </div>
            <div className="sub-images">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="sub-img-item">
                  <img src={product.mediaUrl} alt={product.name} />
                </div>
              ))}
            </div>
          </div>

          {/* Bên phải: Thông tin */}
          <div className="product-detail-right">
            <h1 className="product-title">{product.name}</h1>

            <div className="product-meta-shopee">
              <span className="brand-label">
                Thương hiệu: <span className="blue-text">{product.brand || "Habako"}</span>
              </span>
              <span className="divider">|</span>
              <span className="category-label">
                Danh mục: <span className="blue-text">{product.category}</span>
              </span>
            </div>

            <div className="product-price-box">
              <span className="price-value">Liên hệ báo giá</span>
            </div>

            <div className="product-selection-section">
              <div className="selection-row">
                <span className="label">Số lượng</span>
                <div className="quantity-control">
                  <button onClick={() => handleQuantityChange("minus")}>-</button>
                  <input type="text" value={quantity} readOnly />
                  <button onClick={() => handleQuantityChange("plus")}>+</button>
                </div>
              </div>

              <div className="action-buttons">
                <button className="btn-buy-now" onClick={() => window.open('https://zalo.me/your_number')}>Liên hệ ngay</button>
              </div>
            </div>

            <div className="product-guarantee">
              <span>🛡️ HABAKO Cam kết hàng chính hãng 100%</span>
            </div>
          </div>
        </div>

        {/* Nội dung chi tiết */}
        <div className="product-description-container">
          <h2 className="desc-title">CHI TIẾT SẢN PHẨM</h2>
          <div className="desc-content">
            <p><strong>Thương hiệu:</strong> {product.brand}</p>
            <p><strong>Danh mục:</strong> {product.category}</p>
            {/* Component Info này sẽ chứa dangerouslySetInnerHTML và hàm .replace(/&nbsp;/g, ' ') */}
            <Info product={product} typeName="Thông tin sản phẩm" showFullDescription={true}/>
          </div>
        </div>
      </div>

      {/* Rating truyền productId thực tế */}
      <RatingSection productId={productId} />

      {/* Danh sách sản phẩm liên quan */}
      <section className="related-products" style={{ marginTop: "30px", width: "100%" }}>
        <div style={{ background: "#fff", padding: "20px" }}>
          <div className="related-header-shopee">
            <h2 className="related-title-text">Sản phẩm liên quan</h2>
          </div>

          <div className="products-grid-shopee">
            {relatedProducts.map((item) => (
              <div
                key={item.id}
                className="product-card-shopee reveal-on-scroll"
                onClick={() => {
                  navigate(`/san-pham/${item.slug || createSlug(item.name)}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <div className="product-image">
                  <img src={item.mediaUrl} alt={item.name} />
                  <div className="shopee-badge">Liên quan</div>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{item.name}</h3>
                  <div className="product-price-row">
                    <span className="price-contact">Giá liên hệ</span>
                    <span className="sold-count">Đã xem 1.2k</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;